import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, ScanFace, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { CameraCapture, imageFileBlurScore } from "./CameraCapture";
import { VideoCapture } from "./VideoCapture";

// Ordine canonico di tutti i passaggi possibili; quelli mostrati dipendono dalla
// sessione (required_kinds): base = doc fronte/retro + selfie + video; l'admin può
// richiedere su singola pratica anche il movimento del viso in 3 passaggi (selfie laterali)
const ALL_KINDS = [
  { key: "document_front", label: "id.docFront", testid: "kyc-doc-front-dropzone", capture: "environment", pdf: true },
  { key: "document_back", label: "id.docBack", testid: "kyc-doc-back-dropzone", capture: "environment", pdf: true },
  { key: "selfie_front", label: "id.selfieFront", testid: "kyc-selfie-front-card", capture: "user" },
  { key: "selfie_right", label: "id.selfieRight", testid: "kyc-selfie-right-card", capture: "user" },
  { key: "selfie_left", label: "id.selfieLeft", testid: "kyc-selfie-left-card", capture: "user" },
  { key: "selfie_video", label: "id.selfieVideo", testid: "kyc-selfie-video-card", capture: "user", video: true },
];
const DEFAULT_KINDS = ["document_front", "document_back", "selfie_front", "selfie_video"];

export function IdentityUploadFlow({ token, onSubmitted, onLoaded, stepper = false, voiceOn = false }) {
  const { t, lang } = useLanguage();
  const [session, setSession] = useState(null);
  const sessionStatus = useRef(null);
  const [busyKind, setBusyKind] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await api.get(`/identity/session/${token}`);
      setSession(data.data);
      onLoaded?.(data.data);
      setExpired(false);
    } catch (err) {
      if (err.response?.status === 410) setExpired(true);
    }
  }, [token, onLoaded]);

  useEffect(() => {
    sessionStatus.current = session?.status || null;
  }, [session?.status]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (!sessionStatus.current || sessionStatus.current === "draft") load();
    }, 5000);
    return () => clearInterval(timer);
  }, [load]);

  const redoKinds = session?.redo_kinds || [];
  // passaggi da mostrare: quelli richiesti dalla pratica (base + extra admin)
  const KINDS = ALL_KINDS.filter((k) => (session?.required_kinds || DEFAULT_KINDS).includes(k.key));
  const isMissing = (key) => !session?.files?.[key] || redoKinds.includes(key);
  const currentStep = session && ["draft", "rejected"].includes(session.status)
    ? KINDS.find((item) => isMissing(item.key))
    : null;
  const currentStepKey = currentStep?.key || "";

  useEffect(() => {
    if (!stepper || !voiceOn || !currentStepKey || typeof window === "undefined" || !window.speechSynthesis) return;
    const step = KINDS.find((item) => item.key === currentStepKey);
    if (!step) return;
    const text = `${t(step.label)}. ${step.key === "selfie_video" ? t("id.stepVideo") : step.key.startsWith("selfie") ? t("id.stepSelfie") : t("id.stepDoc")}`;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang === "it" ? "it-IT" : lang === "en" ? "en-US" : lang;
    window.speechSynthesis.speak(utterance);
  }, [stepper, voiceOn, currentStepKey, lang, t]);


  const upload = async (kind, file, options = {}) => {
    if (!file) return;
    setBusyKind(kind);
    try {
      if (!options.skipBlur) {
        const blurScore = await imageFileBlurScore(file).catch(() => null);
        if (blurScore != null && blurScore < 55) {
          toast.error(t("id.blurryToast"));
          return;
        }
      }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/identity/upload/${token}/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession(data.data);
      toast.success(t("id.uploaded"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail === "invalid_file_type" ? t("id.invalidFileType")
        : detail === "file_too_large" ? t("id.fileTooLarge")
        : detail || t("misc.error"));
    } finally {
      setBusyKind(null);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/identity/submit/${token}`);
      setSession(data.data);
      toast.success(t("id.submittedToast"));
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (expired) {
    return <div data-testid="kyc-session-expired" className="rounded-3xl border border-amber-300 bg-amber-50 p-6 text-sm text-amber-800">{t("id.expired")}</div>;
  }
  if (!session) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;

  const status = session.status;
  const editable = status === "draft" || status === "rejected";

  if (stepper) {
    const done = KINDS.length - KINDS.filter((item) => isMissing(item.key)).length;
    return (
      <div data-testid="identity-upload-flow" className="space-y-5">
        {status === "rejected" && (
          <div data-testid="kyc-rejected-note" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {t("id.rejected")}
            {session.review_note && <span className="block mt-1 font-medium">{t("id.rejectedReason")}: {session.review_note}</span>}
            <span data-testid="kyc-redo-list" className="block mt-1 text-xs uppercase tracking-widest font-bold">
              {t("id.redoList")}: {KINDS.filter((item) => isMissing(item.key)).map((item) => t(item.label)).join(" · ")}
            </span>
          </div>
        )}
        {status === "submitted" || status === "approved" ? (
          <div data-testid="kyc-mobile-complete" className="rounded-3xl bg-emerald-50 border border-emerald-200 p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <div className="mt-3 font-display font-bold text-xl text-emerald-800">{t("id.submitted")}</div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2" aria-label={`${done}/${KINDS.length}`}>
              {KINDS.map((item) => (
                <span key={item.key} className={`h-1.5 rounded-full transition-all ${
                  redoKinds.includes(item.key) && session.files?.[item.key] ? "w-8 bg-rose-400" :
                  session.files?.[item.key] ? "w-8 bg-[#0D9488]" : "w-4 bg-slate-200"}`} />
              ))}
            </div>
            {currentStep ? (
              <div className="space-y-4">
                <p data-testid="kyc-mobile-step-guide" className="text-center text-sm text-slate-500">
                  {currentStep.key === "selfie_video" ? t("id.stepVideo") : currentStep.key.startsWith("selfie") ? t("id.stepSelfie") : t("id.stepDoc")}
                </p>
                <UploadSlot item={currentStep} session={session} disabled={!editable} busy={busyKind === currentStep.key}
                  redo={redoKinds.includes(currentStep.key)}
                  note={(session.redo_notes || {})[currentStep.key]} onFile={upload} t={t} />
                <AiDocResult session={session} t={t} />
              </div>
            ) : (
              <div data-testid="kyc-mobile-all-files" className="rounded-3xl bg-emerald-50 border border-emerald-200 p-5 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                <div className="mt-2 font-display font-bold text-emerald-800">{t("id.allFiles")}</div>
              </div>
            )}
            <button type="button" data-testid="kyc-doc-submit-button" onClick={submit}
              disabled={!editable || !session.complete || submitting}
              className="w-full rounded-full bg-[#FF6B35] text-white px-6 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("id.submit")}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div data-testid="identity-upload-flow" className="space-y-6">
      {status === "rejected" && (
        <div data-testid="kyc-rejected-note" className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
          {t("id.rejected")} {session.review_note && <span className="block mt-1 font-medium">{t("id.rejectedReason")}: {session.review_note}</span>}
          <span data-testid="kyc-redo-list" className="block mt-1 text-xs uppercase tracking-widest font-bold">
            {t("id.redoList")}: {KINDS.filter((item) => isMissing(item.key)).map((item) => t(item.label)).join(" · ")}
          </span>
        </div>
      )}
      <div data-testid="kyc-camera-guide-overlay" className="rounded-2xl bg-[#1E293B] text-white px-5 py-4 flex items-start gap-3">
        <ScanFace className="w-5 h-5 text-[#FFB08A] shrink-0 mt-0.5" />
        <p className="text-sm text-slate-200">{t("id.guide")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {KINDS.slice(0, 2).map((item) => (
          <UploadSlot key={item.key} item={item} session={session} disabled={!editable} busy={busyKind === item.key}
            redo={redoKinds.includes(item.key)}
            note={(session.redo_notes || {})[item.key]} onFile={upload} t={t} />
        ))}
      </div>
      <AiDocResult session={session} t={t} />
      <div data-testid="kyc-selfie-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {KINDS.slice(2).map((item) => (
          <UploadSlot key={item.key} item={item} session={session} disabled={!editable} busy={busyKind === item.key}
            redo={redoKinds.includes(item.key)}
            note={(session.redo_notes || {})[item.key]} onFile={upload} t={t} />
        ))}
      </div>

      <button type="button" data-testid="kyc-doc-submit-button" onClick={submit}
        disabled={!editable || !session.complete || submitting}
        className="w-full rounded-full bg-[#FF6B35] text-white px-6 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {status === "submitted" ? t("id.submitted") : t("id.submit")}
      </button>
    </div>
  );
}

function UploadSlot({ item, session, disabled, busy, onFile, t, redo = false, note = null }) {
  const file = session.files?.[item.key];
  const inputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const accept = item.video ? "video/webm,video/mp4,video/quicktime"
    : item.pdf ? "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" : "image/jpeg,image/png,image/webp,image/heic,image/heif";
  return (
    <>
      <div data-testid={item.testid} role="button" tabIndex={disabled ? -1 : 0}
        onClick={() => { if (!disabled && !busy) setCameraOpen(true); }}
        onKeyDown={(e) => { if (e.key === "Enter" && !disabled && !busy) setCameraOpen(true); }}
        className={`rounded-3xl border border-dashed p-5 transition-colors ${
          redo ? "border-rose-400 bg-rose-50/60 ring-1 ring-rose-300" : file ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 bg-white hover:border-[#FF6B35]"} ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}>
        <input ref={inputRef} data-testid={`${item.testid}-input`} type="file" accept={accept} capture={item.capture} className="hidden"
          disabled={disabled || busy} onChange={(e) => onFile(item.key, e.target.files?.[0])} />
        <div className="flex items-start gap-3">
          {busy ? <Loader2 className="w-5 h-5 animate-spin text-[#FF6B35] shrink-0" /> : redo ? <Upload className="w-5 h-5 text-rose-500 shrink-0" /> : file ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <Upload className="w-5 h-5 text-[#FF6B35] shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-slate-900">{t(item.label)}</div>
            {redo && (
              <span data-testid={`${item.testid}-redo-tag`} className="mt-1 inline-block rounded-full bg-rose-600 text-white px-2.5 py-0.5 font-mono-t text-[8px] font-bold uppercase tracking-widest">
                {t("id.redoTag")}
              </span>
            )}
            {redo && note && (
              <div data-testid={`${item.testid}-redo-note`} className="mt-1 text-xs font-medium text-rose-600">{note}</div>
            )}
            <div className="mt-1 text-xs text-slate-500 truncate">{file ? file.filename : t("id.tapToCamera")}</div>
            {!disabled && (
              <button type="button" data-testid={`${item.testid}-file-button`}
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                className="mt-2 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 hover:text-[#FF6B35] transition-colors">
                {t("id.useFile")}
              </button>
            )}
          </div>
        </div>
      </div>
      {cameraOpen && (item.video ? (
        <VideoCapture title={t(item.label)} challenge={session.video_challenge} onClose={() => setCameraOpen(false)}
          onCapture={(videoFile) => { setCameraOpen(false); onFile(item.key, videoFile, { skipBlur: true }); }} />
      ) : (
        <CameraCapture kind={item.key} title={t(item.label)} onClose={() => setCameraOpen(false)}
          onCapture={(fileFromCamera, options) => { setCameraOpen(false); onFile(item.key, fileFromCamera, options); }} />
      ))}
    </>
  );
}

// Esito lettura AI del documento: i dati estratti vengono mostrati subito al cliente
// (li vede e può correggere ricaricando la foto); se la foto non è un documento
// d'identità lo segnala in rosso — il sistema non dice mai "tutto ok" a caso.
function AiDocResult({ session, t }) {
  const frontAi = session.files?.document_front?.ai;
  const dd = session.doc_data;
  if (!frontAi && !dd) return null;
  const notDoc = frontAi && frontAi.is_document === false;
  return (
    <div data-testid="kyc-ai-result" className={`rounded-2xl border p-4 ${notDoc ? "border-rose-300 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
      {notDoc ? (
        <p className="text-sm font-medium text-rose-700">{t("id.aiNotDoc")}</p>
      ) : (
        <>
          <div className="font-display font-bold text-sm text-emerald-900">{t("id.aiTitle")}</div>
          {dd && (
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-emerald-800">
              {dd.full_name && <span data-testid="kyc-ai-name">{t("id.aiName")}: <strong>{dd.full_name}</strong></span>}
              {dd.doc_number && <span data-testid="kyc-ai-docnum">{t("id.aiDoc")}: <strong>{dd.doc_number}</strong></span>}
              {dd.expiry_date && <span>{t("id.aiExpiry")}: <strong>{dd.expiry_date}</strong></span>}
              {dd.birth_date && <span>{t("id.aiBirth")}: <strong>{dd.birth_date}</strong></span>}
              {dd.issuing_country && <span>{t("id.aiCountry")}: <strong>{dd.issuing_country}</strong></span>}
            </div>
          )}
          <p className="mt-2 text-[11px] text-emerald-700">{t("id.aiCheck")}</p>
        </>
      )}
    </div>
  );
}
